import { segment } from '@actions/analytics';
import Button from '@base/Button/Button';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import { evtNameCategoryMap, programmeTypes } from '@constants/index';
import {
  ADD_TO_PLANNER_PREFERENCE_QUESTION,
  COURSE_KEYS,
  TEST_SERIES_KEYS
} from '@constants/segment';
import CourseTimeSelector from '@cont/Course/CourseHeader/AddCourseToPlanner/CourseTimeSelector';
import styled from '@emotion/styled';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { Spacings } from '@styles/index';
import { getLabelForTimeinDay } from '@utils/dates';
import { FC, useState } from 'react';
import { AnyArgVoidReturnFunction } from 'types/utils';
import { CustomisationStepsInfo } from './AllCustomizationSteps';

const HeadingWrapper = styled.div`
  margin-top: ${Spacings.SPACING_3B};
`;

const StyledP1 = styled(P1)`
  margin-top: ${Spacings.SPACING_4B};
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
`;

const ConfirmButton = styled(Button)<any>`
  background-color: var(--color-text-primary);
  color: var(--color-base-1);

  &:hover,
  &:active {
    background-color: var(--color-text-primary);
  }
`;

const CustomizeMoreButton = styled(Button)<any>`
  color: var(--color-text-primary);
  background-color: var(--color-base-1);
`;

export interface IBaseAddDrawerProps {
  plannerSchedule: any;
  onClickConfirm: AnyArgVoidReturnFunction;
  isEditFlow?: boolean;
  goalUid: string;
  courseType: number;
  plusCourse: string;
}

interface IAddDrawerProps extends IBaseAddDrawerProps {
  onClickCustomise: AnyArgVoidReturnFunction;
}

const CustomizeAddDrawerContent: FC<IAddDrawerProps> = ({
  isEditFlow = false,
  goalUid,
  courseType,
  plusCourse,
  onClickCustomise,
  onClickConfirm,
  plannerSchedule
}) => {
  const [selectedChoice, setSelectedChoice] = useState(
    plannerSchedule?.daily_item_schedule_at || 540
  );
  const goalSegmentData = useGoalEventProps({ goalUID: goalUid });
  const segmentData = useAllTypesOfCourseEventProps({ course: plusCourse });
  const eventKeys =
    courseType === programmeTypes.TESTSERIES ? TEST_SERIES_KEYS : COURSE_KEYS;

  const handleConfirm = () => {
    onClickConfirm({ selectedChoice });
    const currentStep = CustomisationStepsInfo.LESSON_TIME;
    const responseText = getLabelForTimeinDay(selectedChoice / 60);
    segment.track(
      ADD_TO_PLANNER_PREFERENCE_QUESTION,
      {
        ...goalSegmentData,
        ...segmentData,
        is_default_preference: true,
        question_content: currentStep.title,
        response_text: responseText,
        is_first_time: !isEditFlow,
        is_skipped: false
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
  };

  const preferredTime = selectedChoice === 0 ? 24 : selectedChoice / 60;
  const onChange = (timeInMinutes: number) => setSelectedChoice(timeInMinutes);

  return (
    <>
      <HeadingWrapper>
        <H3>Choose your preferred time</H3>
        <StyledP1>
          We&apos;ll add 1 class every day, at{' '}
          {getLabelForTimeinDay(preferredTime)}
        </StyledP1>
      </HeadingWrapper>
      <CourseTimeSelector selectedChoice={selectedChoice} onChange={onChange} />
      <ActionContainer>
        <CustomizeMoreButton
          variant="hollow"
          label="Customize more"
          onClick={onClickCustomise}
        />
        <ConfirmButton color="black" label="Confirm" onClick={handleConfirm} />
      </ActionContainer>
    </>
  );
};

export default CustomizeAddDrawerContent;
